import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Warehouse as WarehouseIcon, Plus, Pencil, Trash2 } from "lucide-react";
import { WarehouseDeleteDialog } from "@/components/WarehouseDeleteDialog";
import { currentUserIsAdmin } from "@/components/mcf/mcfUtils";
import { toast } from "sonner";

const TYPE_LABEL = {
  weekly_allocation: "Weekly Allocation",
  fbw_inventory: "FBW Inventory",
};

const emptyForm = {
  warehouse_name: "",
  warehouse_code: "",
  warehouse_type: "fbw_inventory",
  address: "",
  city: "",
  state: "",
  is_active: true,
};

export default function WarehouseMaster() {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/warehouses`, {
        params: { include_inactive: true },
        withCredentials: true,
      });
      setWarehouses(res.data || []);
    } catch {
      toast.error("Error fetching warehouses");
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (w) => {
    setEditing(w);
    setForm({
      warehouse_name: w.warehouse_name,
      warehouse_code: w.warehouse_code,
      warehouse_type: w.warehouse_type,
      address: w.address || "",
      city: w.city || "",
      state: w.state || "",
      is_active: w.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.warehouse_name.trim() || !form.warehouse_code.trim()) {
      toast.error("Warehouse name and code are required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/warehouses/${editing.id}`, form, { withCredentials: true });
        toast.success("Warehouse updated");
      } else {
        await axios.post(`${API}/warehouses`, form, { withCredentials: true });
        toast.success("Warehouse added");
      }
      setDialogOpen(false);
      fetchWarehouses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving warehouse");
    } finally {
      setSaving(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const isAdmin = currentUserIsAdmin();

  const toggleActive = async (w) => {
    try {
      await axios.put(`${API}/warehouses/${w.id}`, { is_active: !w.is_active }, { withCredentials: true });
      toast.success(`${w.warehouse_name} ${!w.is_active ? "activated" : "deactivated"}`);
      fetchWarehouses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error updating warehouse");
    }
  };

  return (
    <Card data-testid="warehouse-master-card">
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <WarehouseIcon className="h-5 w-5 text-accent" />
            Warehouse Master
          </CardTitle>
          <p className="text-sm text-muted-foreground pt-1">
            Manage warehouses and their type. <strong>Weekly Allocation</strong> uses the weekly inventory model;
            <strong> FBW Inventory</strong> maintains SKU-wise available stock per warehouse. Admin-only.
          </p>
        </div>
        <Button onClick={openAdd} className="bg-accent hover:bg-accent/90 text-white" data-testid="add-warehouse-button">
          <Plus className="h-4 w-4 mr-2" />
          Add Warehouse
        </Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Warehouse Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>City</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-center w-[110px]">Active</TableHead>
                <TableHead className="text-right w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</TableCell>
                </TableRow>
              ) : warehouses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No warehouses configured.</TableCell>
                </TableRow>
              ) : (
                warehouses.map((w) => (
                  <TableRow key={w.id} data-testid={`warehouse-row-${w.warehouse_code}`}>
                    <TableCell className="font-medium">{w.warehouse_name}</TableCell>
                    <TableCell className="font-mono text-sm">{w.warehouse_code}</TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${w.warehouse_type === "weekly_allocation" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800"}`}>
                        {TYPE_LABEL[w.warehouse_type] || w.warehouse_type}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[240px] truncate">{w.address || "—"}</TableCell>
                    <TableCell className="text-sm" data-testid={`warehouse-city-${w.warehouse_code}`}>{w.city || "—"}</TableCell>
                    <TableCell className="text-sm" data-testid={`warehouse-state-${w.warehouse_code}`}>{w.state || "—"}</TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={w.is_active}
                        onCheckedChange={() => toggleActive(w)}
                        data-testid={`warehouse-active-toggle-${w.warehouse_code}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(w)} data-testid={`edit-warehouse-${w.warehouse_code}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600 hover:text-rose-700" onClick={() => setDeleteTarget(w)} title="Delete" data-testid={`delete-warehouse-${w.warehouse_code}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <WarehouseDeleteDialog warehouse={deleteTarget} onClose={() => setDeleteTarget(null)} onDone={() => { setDeleteTarget(null); fetchWarehouses(); }} />
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="warehouse-dialog-title">{editing ? "Edit Warehouse" : "Add Warehouse"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="wh-name">Warehouse Name *</Label>
              <Input id="wh-name" value={form.warehouse_name} onChange={(e) => setForm({ ...form, warehouse_name: e.target.value })} data-testid="warehouse-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wh-code">Warehouse Code *</Label>
              <Input id="wh-code" value={form.warehouse_code} onChange={(e) => setForm({ ...form, warehouse_code: e.target.value })} data-testid="warehouse-code-input" />
            </div>
            <div className="space-y-2">
              <Label>Warehouse Type *</Label>
              <Select value={form.warehouse_type} onValueChange={(v) => setForm({ ...form, warehouse_type: v })}>
                <SelectTrigger data-testid="warehouse-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly_allocation">Weekly Allocation</SelectItem>
                  <SelectItem value="fbw_inventory">FBW Inventory</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="wh-address">Address</Label>
              <Input id="wh-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="warehouse-address-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="wh-city">City</Label>
                <Input id="wh-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="warehouse-city-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wh-state">State</Label>
                <Input id="wh-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="warehouse-state-input" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="wh-active">Active</Label>
              <Switch id="wh-active" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="warehouse-active-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-accent hover:bg-accent/90 text-white" data-testid="save-warehouse-button">
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button onClick={() => setDialogOpen(false)} variant="outline" className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
